import { computed, makeAutoObservable } from 'mobx';
import { observable }                   from 'mobx';
import { ApiCollection }                from 'collections/ApiCollection';
import { ROLE_ORGANIZER }               from 'constants/RoleReferences';
import DemandTypeModel                  from 'models/DemandTypeModel';
import UserShowModel                    from 'models/UserShowModel';
import { STATUS_CONVERTER }             from 'utils/constants';
import ProviderShowModel                from 'models/ProviderShowModel';
import AdvertiserShowModel              from 'models/AdvertiserShowModel';
import DemandConfigModel                from 'models/DemandConfigModel';
import UserModel                        from 'models/UserModel';
import ShowModel                        from 'models/ShowModel';
import SourceModel                      from 'models/SourceModel';
import StandModel                       from 'models/StandModel';

export default class ShowDashboardStore {
  @observable public advertiserShowCollection = new ApiCollection(AdvertiserShowModel);
  @observable public demandConfigCollection = new ApiCollection(DemandConfigModel);
  @observable public demandTypeCollection = new ApiCollection(DemandTypeModel);
  @observable public organizerModel = new UserModel();
  @observable public organizerUserCollection = new ApiCollection(UserModel);
  @observable public organizerUserShowCollection = new ApiCollection(UserShowModel);
  @observable public providerShowCollection = new ApiCollection(ProviderShowModel);
  @observable public showModel = new ShowModel();
  @observable public source = new SourceModel();
  @observable public standCollection = new ApiCollection(StandModel);
  @observable public standCollectionPendingConfirmation = new ApiCollection(StandModel);
  @observable public standCollectionPendingVerification = new ApiCollection(StandModel);
  @observable public standCollectionUnSeen = new ApiCollection(StandModel);
  @observable public standCollectionValidated = new ApiCollection(StandModel);
  @observable public standCollectionWaiting = new ApiCollection(StandModel);

  constructor() {
    makeAutoObservable(this);
  }

  public load = async (showId: id) => {
    this.showModel.setId(showId);

    await this.showModel.fetch();

    this.source.clear();
    const sourceId = this.showModel.get('source.id');
    if (this.showModel.externalSync && sourceId) {
      this.source.setId(sourceId);
      await this.source.fetch();
    }

    await this.loadOrganizer(showId);

    await Promise.all([
      this.demandTypeCollection.list(),

      this.demandConfigCollection.setFilter('show', showId).list(),

      this.loadStandCollection(showId),

      this.providerShowCollection.setFilter('show', showId).list(),

      this.advertiserShowCollection.setFilter('show', showId).list(),
    ]);
  };

  public loadOrganizer = async (showId: id) => {
    this.organizerUserShowCollection.clear();
    this.organizerModel.clear();

    this.organizerUserShowCollection.setFilter('show', showId);
    this.organizerUserShowCollection.setFilter('user.role.reference', ROLE_ORGANIZER);

    await this.organizerUserShowCollection.list();

    const organizerId = this.organizerUserShowCollection.first()?.get('user.id');
    if (organizerId) {
      this.organizerModel.set('id', organizerId);
      await this.organizerModel.fetch();
    }
  };

  public loadStandCollection = async (showId: id) => {
    this.standCollectionValidated.setFilter('status', STATUS_CONVERTER.validated);
    this.standCollectionUnSeen.setFilter('unSeen', true);
    this.standCollectionWaiting.setFilter('status', STATUS_CONVERTER.waiting);
    this.standCollectionPendingVerification.setFilter(
      'status',
      STATUS_CONVERTER.pendingVerification,
    );
    this.standCollectionPendingConfirmation.setFilter(
      'status',
      STATUS_CONVERTER.pendingConfirmation,
    );

    await Promise.all([
      this.standCollection
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
      this.standCollectionValidated
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
      this.standCollectionUnSeen
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
      this.standCollectionWaiting
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
      this.standCollectionPendingVerification
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
      this.standCollectionPendingConfirmation
        .setFilter('show', showId)
        .setFilter('itemsPerPage', 0)
        .list(),
    ]);
  };

  @computed
  get userShow() {
    const userShow = this.organizerUserShowCollection.first();

    if (!(userShow instanceof UserShowModel)) {
      return new UserShowModel();
    }

    return userShow;
  }
}
